import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from "react-router";
import {AuthModel} from "../model/auth-model";
import {HTTP_STATUS_CODE} from "../utils/UHttp";
import {useLocation} from "react-router-dom";
import {useProfessionalViewModel} from "../view-model/professional/professional-view-model";
import companyViewModel from "../view-model/professional/company/company-view-model";

// Types
type AccountType = 'client' | 'provider';

interface AuthContextType {
  accountType: AccountType;
}

// Valeur par défaut du contexte
const AuthContext = createContext<AuthContextType>({
  accountType: 'provider',
});

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => useContext(AuthContext);

// Provider du contexte d'authentification
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const authModel = AuthModel.getInstance();
  const location = useLocation();
  const professionalViewModel = useProfessionalViewModel()

  // Liste des chemins exemptés de redirection automatique
  const exemptPaths = ['/verify-email', '/reset-password'];

  // Fonction pour vérifier si le chemin actuel est exempté
  const isExemptPath = () => {
    return exemptPaths.some(path => location.pathname.startsWith(path));
  };

  useEffect(() => {
    // Vérifier si l'utilisateur est déjà connecté au chargement de l'application
    const checkAuth = async () => {
      try {
        const token = Cookies.get("MODESSID");
        if (token) {
            const response = await authModel.handleVerifyToken();
            if (response.status === HTTP_STATUS_CODE.OK) {
              // Ne récupérer le profil que si l'utilisateur accède à une route protégée du provider
              // ou s'il tente d'accéder à une route qui nécessite une redirection vers le dashboard
              const isProviderRoute = location.pathname.startsWith('/provider');
              const needsRedirect = !isExemptPath() && (location.pathname === '/' || location.pathname === '/login' || location.pathname === '/signup');

              // Étape 1 : Récupérer la liste des compagnies
              await companyViewModel.handleGetAllCompaniesByCollaborator();
              
              if (isProviderRoute || needsRedirect) {
                await professionalViewModel.handleGetProfil();
              }
              
              // Rediriger seulement si le chemin actuel n'est pas exempté
              if (needsRedirect) {

                navigate("/provider/dashboard");
              }
          }
        } else if (!isExemptPath() && location.pathname.startsWith('/provider')) {
          const pathParts = location.pathname.split('/');
          const currentLang = pathParts.length > 1 && pathParts[1].length === 2 ? pathParts[1] : 'fr';
          navigate(`/${currentLang}/login`);
        }
      } catch (error: any) {

        // Rediriger seulement si le chemin actuel n'est pas exempté
        if (!isExemptPath()) {
          const pathParts = location.pathname.split('/');
          const currentLang = pathParts.length > 1 && pathParts[1].length === 2 ? pathParts[1] : 'fr';
          navigate(`/${currentLang}/login`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth().then();
  }, [navigate, location.pathname]);

  const value: AuthContextType = {
    accountType: "provider" as AccountType,
  };

  if (isLoading) {
    return (
        <div
            className="flex justify-center items-center min-h-screen bg-gradient-to-br from-white via-primary/5 to-white relative overflow-hidden">
          {/* Éléments décoratifs pour le loader */}
          <div className="absolute left-0 top-1/4 w-64 h-64 bg-primary/5 rounded-full -z-10 blur-3xl"></div>
          <div className="absolute right-0 bottom-1/4 w-64 h-64 bg-primary/5 rounded-full -z-10 blur-3xl"></div>
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-primary"></div>
        </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
